import { Module } from 'vuex'

import { GetByEntryIdReviewListUseCase } from '@/application/review/getByEntryIdReviewListUseCase'
import { PostCloseEntryUseCase } from '@/application/review/postCloseEntryUseCase'
import { PostEvaluationReviewUseCase } from '@/application/review/postEvaluationReviewUseCase'
import { ReviewEntryDetail } from '@/domain/entities/review/reviewEntryDetail'
import { ReviewEntryRepository } from '@/domain/repositories/review/reviewEntryRepository'

interface ReviewEntryDetailState {
  reviewEntryDetailList: { [entryId: string]: ReviewEntryDetail[] }
  isLoading: boolean
  error: Error | null
}

export const reviewEntryDetailModule = (
  reviewEntryRepository: ReviewEntryRepository
): Module<ReviewEntryDetailState, any> => {
  const getByEntryIdReviewsUseCase = new GetByEntryIdReviewListUseCase(reviewEntryRepository)
  const postCloseEntryUseCase = new PostCloseEntryUseCase(reviewEntryRepository)
  const postEvaluationReviewUseCase = new PostEvaluationReviewUseCase(reviewEntryRepository)

  return {
    namespaced: true,
    state: {
      reviewEntryDetailList: {},
      isLoading: false,
      error: null
    },
    mutations: {
      setReviewEntryDetailList(state, { detailList }: { detailList: { [entryId: string]: ReviewEntryDetail[] } }) {
        state.reviewEntryDetailList = detailList
      },
      setReviewEntryDetailListByEntryId(
        state,
        { entryId, detailList }: { entryId: string; detailList: ReviewEntryDetail[] }
      ) {
        state.reviewEntryDetailList[entryId] = detailList
      },
      setLoading(state, isLoading: boolean) {
        state.isLoading = isLoading
      },
      setError(state, error: Error | null) {
        state.error = error
      }
    },
    actions: {
      async fetchReviewEntryDetailList({ state, commit }, { entryId }: { entryId: string }) {
        commit('setLoading', true)
        commit('setError', null)
        try {
          const detailList = await getByEntryIdReviewsUseCase.getByEntryIdReviews(entryId)
          commit('setReviewEntryDetailListByEntryId', { entryId, detailList })
        } catch (e) {
          commit('setError', e)
          commit('setReviewEntryDetailListByEntryId', { entryId, detailList: [] })
        } finally {
          commit('setLoading', false)
        }
      },
      async postCloseEntry({ state, commit }, { entryId }: { entryId: string }) {
        commit('setError', null)
        try {
          await postCloseEntryUseCase.postCloseEntry(entryId)
        } catch (e) {
          commit('setError', e)
        }
      },
      async postEvaluationReview({ state, commit }, { reviewId, score }: { reviewId: string; score: number }) {
        commit('setError', null)
        try {
          await postEvaluationReviewUseCase.postEvaluationReview(reviewId, score)
        } catch (e) {
          commit('setError', e)
        }
      }
    },
    getters: {
      reviewEntryDetailList: state => state.reviewEntryDetailList,
      isLoading: state => state.isLoading,
      error: state => state.error
    }
  }
}
